/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';

/**
 * Placeholder icons for each remix sub-type.
 *
 * All icons render at a consistent 32×32 size so the sub-type grid
 * has uniform tile heights regardless of category.
 *
 * These use Atlaskit design-token colours so they adapt to light / dark mode.
 */

const ICON_SIZE = 32;

// ── Charts ──────────────────────────────────────────────────────────────────

export const LineChartIcon = () => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 32 32" fill="none" aria-hidden="true">
		<path
			d="M4 24L11 16L18 20L28 8"
			stroke={token('color.icon.accent.blue')}
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
	</svg>
);

export const AreaChartIcon = () => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 32 32" fill="none" aria-hidden="true">
		<path
			d="M4 24L11 16L18 20L28 8V24H4Z"
			fill={token('color.background.accent.blue.subtler')}
		/>
		<path
			d="M4 24L11 16L18 20L28 8"
			stroke={token('color.icon.accent.blue')}
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
	</svg>
);

export const BarChartIcon = () => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 32 32" fill="none" aria-hidden="true">
		<rect x="4" y="18" width="5" height="8" rx="1" fill={token('color.icon.accent.blue')} />
		<rect x="11" y="12" width="5" height="14" rx="1" fill={token('color.icon.accent.teal')} />
		<rect x="18" y="8" width="5" height="18" rx="1" fill={token('color.icon.accent.blue')} />
		<rect x="25" y="14" width="5" height="12" rx="1" fill={token('color.icon.accent.teal')} />
	</svg>
);

export const FunnelChartIcon = () => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 32 32" fill="none" aria-hidden="true">
		<path d="M2 6H30L22 16L20 26H12L10 16L2 6Z" fill={token('color.icon.accent.blue')} />
	</svg>
);

export const PieChartIcon = () => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 32 32" fill="none" aria-hidden="true">
		<circle cx="16" cy="16" r="11" fill={token('color.background.accent.blue.subtler')} />
		<path
			d="M16 5A11 11 0 0 1 27 16H16V5Z"
			fill={token('color.icon.accent.blue')}
		/>
		<path
			d="M16 16L24.5 23A11 11 0 0 1 7.5 23L16 16Z"
			fill={token('color.icon.accent.teal')}
		/>
	</svg>
);

export const GanttChartIcon = () => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 32 32" fill="none" aria-hidden="true">
		<rect x="4" y="6" width="18" height="4" rx="1" fill={token('color.icon.accent.blue')} />
		<rect x="8" y="13" width="14" height="4" rx="1" fill={token('color.icon.accent.teal')} />
		<rect x="12" y="20" width="16" height="4" rx="1" fill={token('color.icon.accent.blue')} />
	</svg>
);

// ── Diagrams ────────────────────────────────────────────────────────────────

export const MindMapIcon = () => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 32 32" fill="none" aria-hidden="true">
		<circle cx="16" cy="16" r="4" fill={token('color.icon.accent.purple')} />
		<circle cx="6" cy="8" r="3" fill={token('color.icon.accent.blue')} />
		<circle cx="26" cy="8" r="3" fill={token('color.icon.accent.blue')} />
		<circle cx="6" cy="24" r="3" fill={token('color.icon.accent.teal')} />
		<circle cx="26" cy="24" r="3" fill={token('color.icon.accent.teal')} />
		<line x1="13" y1="13" x2="8" y2="10" stroke={token('color.border')} strokeWidth="1.5" />
		<line x1="19" y1="13" x2="24" y2="10" stroke={token('color.border')} strokeWidth="1.5" />
		<line x1="13" y1="19" x2="8" y2="22" stroke={token('color.border')} strokeWidth="1.5" />
		<line x1="19" y1="19" x2="24" y2="22" stroke={token('color.border')} strokeWidth="1.5" />
	</svg>
);

export const FlowChartIcon = () => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 32 32" fill="none" aria-hidden="true">
		<rect x="10" y="2" width="12" height="7" rx="1.5" fill={token('color.icon.accent.blue')} />
		<rect x="10" y="23" width="12" height="7" rx="1.5" fill={token('color.icon.accent.teal')} />
		<path
			d="M16 12.5L21 16L16 19.5L11 16L16 12.5Z"
			fill={token('color.icon.accent.purple')}
		/>
		<line x1="16" y1="9" x2="16" y2="12.5" stroke={token('color.border')} strokeWidth="1.5" />
		<line x1="16" y1="19.5" x2="16" y2="23" stroke={token('color.border')} strokeWidth="1.5" />
	</svg>
);

// ── Infographics ────────────────────────────────────────────────────────────

export const ClassicStyleIcon = () => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 32 32" fill="none" aria-hidden="true">
		<rect x="3" y="3" width="26" height="26" rx="3" stroke={token('color.border')} strokeWidth="1.5" fill="none" />
		<rect x="6" y="7" width="10" height="2" rx="1" fill={token('color.icon.accent.blue')} />
		<rect x="6" y="12" width="20" height="1.5" rx="0.75" fill={token('color.border')} />
		<rect x="6" y="16" width="20" height="1.5" rx="0.75" fill={token('color.border')} />
		<rect x="6" y="20" width="14" height="1.5" rx="0.75" fill={token('color.border')} />
		<rect x="6" y="24" width="18" height="1.5" rx="0.75" fill={token('color.border')} />
	</svg>
);

export const SketchStyleIcon = () => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 32 32" fill="none" aria-hidden="true">
		<rect x="3" y="3" width="26" height="26" rx="5" stroke={token('color.border')} strokeWidth="1.5" strokeDasharray="4 2" fill="none" />
		<path d="M7 8C9 7 12 9 16 8C20 7 23 9 25 8" stroke={token('color.icon.accent.purple')} strokeWidth="1.5" strokeLinecap="round" fill="none" />
		<path d="M7 13C10 14 14 12 18 13C22 14 24 12 25 13" stroke={token('color.border')} strokeWidth="1.5" strokeLinecap="round" fill="none" />
		<path d="M7 18C10 17 13 19 17 18C21 17 24 19 25 18" stroke={token('color.border')} strokeWidth="1.5" strokeLinecap="round" fill="none" />
		<path d="M7 23C11 24 15 22 19 23" stroke={token('color.border')} strokeWidth="1.5" strokeLinecap="round" fill="none" />
	</svg>
);

// ── Visual Aids ─────────────────────────────────────────────────────────────

export const FlipCardIcon = () => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 32 32" fill="none" aria-hidden="true">
		<rect x="2" y="4" width="13" height="18" rx="2" fill={token('color.background.accent.blue.subtler')} stroke={token('color.border')} strokeWidth="1" />
		<rect x="17" y="4" width="13" height="18" rx="2" fill={token('color.icon.accent.blue')} />
		<path d="M12 25L16 28L20 25" stroke={token('color.border')} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
	</svg>
);

export const QuadrantIcon = () => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 32 32" fill="none" aria-hidden="true">
		<line x1="16" y1="4" x2="16" y2="28" stroke={token('color.border')} strokeWidth="1.5" />
		<line x1="4" y1="16" x2="28" y2="16" stroke={token('color.border')} strokeWidth="1.5" />
		<circle cx="10" cy="10" r="3" fill={token('color.icon.accent.blue')} />
		<circle cx="22" cy="10" r="3" fill={token('color.icon.accent.teal')} />
		<circle cx="10" cy="22" r="3" fill={token('color.icon.accent.purple')} />
		<circle cx="22" cy="22" r="3" fill={token('color.icon.accent.orange')} />
	</svg>
);

export const TreeMapIcon = () => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 32 32" fill="none" aria-hidden="true">
		<rect x="3" y="3" width="14" height="26" rx="1.5" fill={token('color.icon.accent.blue')} />
		<rect x="19" y="3" width="10" height="14" rx="1.5" fill={token('color.icon.accent.teal')} />
		<rect x="19" y="19" width="10" height="10" rx="1.5" fill={token('color.icon.accent.purple')} />
	</svg>
);

export const TimelineIcon = () => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 32 32" fill="none" aria-hidden="true">
		<line x1="4" y1="16" x2="28" y2="16" stroke={token('color.border')} strokeWidth="1.5" />
		<circle cx="8" cy="16" r="3" fill={token('color.icon.accent.blue')} />
		<circle cx="16" cy="16" r="3" fill={token('color.icon.accent.teal')} />
		<circle cx="24" cy="16" r="3" fill={token('color.icon.accent.purple')} />
		<rect x="5" y="6" width="6" height="3" rx="1" fill={token('color.background.accent.blue.subtler')} />
		<rect x="13" y="23" width="6" height="3" rx="1" fill={token('color.background.accent.teal.subtler')} />
		<rect x="21" y="6" width="6" height="3" rx="1" fill={token('color.background.accent.purple.subtler')} />
	</svg>
);

export const GeographicalMapIcon = () => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 32 32" fill="none" aria-hidden="true">
		<circle cx="16" cy="16" r="12" stroke={token('color.border')} strokeWidth="1.5" fill="none" />
		<ellipse cx="16" cy="16" rx="6" ry="12" stroke={token('color.border')} strokeWidth="1" fill="none" />
		<line x1="4" y1="12" x2="28" y2="12" stroke={token('color.border')} strokeWidth="1" />
		<line x1="4" y1="20" x2="28" y2="20" stroke={token('color.border')} strokeWidth="1" />
		<circle cx="12" cy="12" r="2" fill={token('color.icon.accent.blue')} />
		<circle cx="20" cy="18" r="2" fill={token('color.icon.accent.teal')} />
	</svg>
);

export const OrgChartIcon = () => (
	<svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 32 32" fill="none" aria-hidden="true">
		<rect x="11" y="3" width="10" height="7" rx="1.5" fill={token('color.icon.accent.blue')} />
		<rect x="2" y="22" width="8" height="7" rx="1.5" fill={token('color.icon.accent.teal')} />
		<rect x="12" y="22" width="8" height="7" rx="1.5" fill={token('color.icon.accent.teal')} />
		<rect x="22" y="22" width="8" height="7" rx="1.5" fill={token('color.icon.accent.teal')} />
		<line x1="16" y1="10" x2="16" y2="16" stroke={token('color.border')} strokeWidth="1.5" />
		<line x1="6" y1="16" x2="26" y2="16" stroke={token('color.border')} strokeWidth="1.5" />
		<line x1="6" y1="16" x2="6" y2="22" stroke={token('color.border')} strokeWidth="1.5" />
		<line x1="16" y1="16" x2="16" y2="22" stroke={token('color.border')} strokeWidth="1.5" />
		<line x1="26" y1="16" x2="26" y2="22" stroke={token('color.border')} strokeWidth="1.5" />
	</svg>
);

import type { KnownSubType } from './types';

/**
 * Maps each sub-type value to its icon component.
 * Used by the sub-type grid to render the correct icon for each tile.
 *
 * Typed as `Record<KnownSubType, …>` so a missing entry is a compile-time error.
 */
export const SUB_TYPE_ICON_MAP: Record<KnownSubType, () => JSX.Element> = {
	// Charts
	'line-chart': LineChartIcon,
	'area-chart': AreaChartIcon,
	'bar-chart': BarChartIcon,
	'funnel-chart': FunnelChartIcon,
	'pie-chart': PieChartIcon,
	'gantt-chart': GanttChartIcon,
	// Diagrams
	mindmap: MindMapIcon,
	flowchart: FlowChartIcon,
	// Infographics
	'corporate-modern': ClassicStyleIcon,
	'corporate-doodle': SketchStyleIcon,
	// Visual aids
	'flip-cards': FlipCardIcon,
	quadrant: QuadrantIcon,
	'tree-map': TreeMapIcon,
	'horizontal-timeline': TimelineIcon,
	map: GeographicalMapIcon,
	'org-chart': OrgChartIcon,
};
